import type { SignInReq } from '@/api/services/userService';
import { FcGoogle } from 'react-icons/fc';
import { useSignIn } from '@/store/userStore';
import { Button } from '@/ui/button';
import { Checkbox } from '@/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/form';
import { Input } from '@/ui/input';
import { cn } from '@/utils';
import { ArrowRight, Lock, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function LoginForm({ className, ...props }: React.ComponentPropsWithoutRef<'form'>) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);

  const isProduction = import.meta.env.APP_ENVIRONMENT === 'production';

  const signIn = useSignIn();

  const form = useForm<SignInReq>({
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const handleFinish = async (values: SignInReq) => {
    if (loading) return;
    setLoading(true);
    try {
      await signIn(values);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <Form {...form} {...props}>
        <form onSubmit={form.handleSubmit(handleFinish)} className="space-y-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-semibold text-[#121926] dark:text-white">
              {t('sys.login.signInFormTitle')}
            </h2>
            <p className="text-sm leading-6 text-[#667085] dark:text-white/58">
              {isProduction
                ? t('sys.login.signInFormDescriptionPrd')
                : t('sys.login.signInFormDescription')}
            </p>
          </div>

          {!isProduction && (
            <>
              <FormField
                control={form.control}
                name="username"
                rules={{ required: t('sys.login.accountPlaceholder') }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-[#344054] dark:text-white/78">
                      {t('sys.login.userName')}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <UserRound className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#98a2b3]" />
                        <Input
                          placeholder="admin/test"
                          className="h-11 rounded-lg bg-[#f8fafc] pl-10 text-sm dark:bg-white/5"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                rules={{ required: t('sys.login.passwordPlaceholder') }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-[#344054] dark:text-white/78">
                      {t('sys.login.password')}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#98a2b3]" />
                        <Input
                          type="password"
                          placeholder={t('sys.login.password')}
                          className="h-11 rounded-lg bg-[#f8fafc] pl-10 text-sm dark:bg-white/5"
                          {...field}
                          suppressHydrationWarning
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-row justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember"
                    checked={remember}
                    onCheckedChange={(checked) =>
                      setRemember(checked === 'indeterminate' ? false : checked)
                    }
                  />
                  <label
                    htmlFor="remember"
                    className="text-sm font-medium leading-none text-[#475467] peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white/62"
                  >
                    {t('sys.login.rememberMe')}
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                loading={loading}
                className="h-11 w-full rounded-lg bg-[#1f8f66] font-semibold text-white shadow-[0_12px_28px_rgba(31,143,102,0.28)] hover:bg-[#187a57]"
              >
                {t('sys.login.loginButton')}
                {!loading && <ArrowRight className="ml-1 size-4" />}
              </Button>
            </>
          )}

          {!isProduction && (
            <div className="flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-[#e4e7ec] dark:bg-white/10" />
              <span className="text-xs font-medium text-[#98a2b3]">or</span>
              <div className="h-px flex-1 bg-[#e4e7ec] dark:bg-white/10" />
            </div>
          )}

          <Button
            variant="outline"
            type="button"
            className="h-11 w-full cursor-pointer rounded-lg border-[#d0d5dd] bg-white font-semibold text-[#344054] shadow-sm transition-colors hover:bg-[#f8fafc] dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
            onClick={() => {
              window.location.href = `${import.meta.env.APP_BASE_API}/auth/google`;
            }}
          >
            <span className="flex items-center gap-2">
              <FcGoogle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {t('sys.login.loginWithGoogle') || 'Login with Google'}
              </span>
            </span>
          </Button>
        </form>
      </Form>
    </div>
  );
}

export default LoginForm;
